'use client';

import type { KeyboardEvent } from 'react';
import { Children, isValidElement, useEffect, useId, useMemo, useRef, useState } from 'react';
import { cn } from '@/lib/tailwind';
import type { UiSliderProps } from './UiSlider.types';
import { UiSliderNavigation } from './UiSliderNavigation';

export function UiSlider({ children, className, listClassName, itemClassName, navigationClassName }: UiSliderProps) {
    const items = useMemo(() => Children.toArray(children), [children]);
    const scrollRef = useRef<HTMLDivElement | null>(null);
    const listId = useId();

    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);

    function getEls() {
        const el = scrollRef.current;
        if (!el) return null;
        const li = Array.from(el.querySelectorAll('li')) as HTMLLIElement[];
        return { el, li };
    }

    function scrollByStep(dir: -1 | 1) {
        const els = getEls();
        if (!els) return;

        const { el, li } = els;
        if (!li.length) return;

        const containerLeft = el.getBoundingClientRect().left;
        const epsilon = 4;

        if (dir === 1) {
            for (let i = 0; i < li.length; i++) {
                const offset = li[i].getBoundingClientRect().left - containerLeft;
                if (offset > epsilon) {
                    el.scrollBy({ left: offset, behavior: 'smooth' });
                    return;
                }
            }
            el.scrollTo({ left: el.scrollWidth, behavior: 'smooth' });
        } else {
            for (let i = li.length - 1; i >= 0; i--) {
                const offset = li[i].getBoundingClientRect().left - containerLeft;
                if (offset < -epsilon) {
                    el.scrollBy({ left: offset, behavior: 'smooth' });
                    return;
                }
            }
            el.scrollTo({ left: 0, behavior: 'smooth' });
        }
    }

    function scrollPrev() {
        scrollByStep(-1);
    }

    function scrollNext() {
        scrollByStep(1);
    }

    function onKeyDown(e: KeyboardEvent<HTMLDivElement>) {
        if (e.key === 'ArrowLeft') {
            e.preventDefault();
            scrollPrev();
        } else if (e.key === 'ArrowRight') {
            e.preventDefault();
            scrollNext();
        }
    }

    useEffect(() => {
        let raf = 0;
        let attached = false;
        let ro: ResizeObserver | null = null;

        function update() {
            const els = getEls();
            if (!els) return;

            const { el: scrollEl, li } = els;
            const last = li[li.length - 1];
            if (!last) return;

            const epsilon = 4;
            const viewportRect = scrollEl.getBoundingClientRect();
            const lastRect = last.getBoundingClientRect();

            setCanScrollLeft(scrollEl.scrollLeft > epsilon);
            setCanScrollRight(lastRect.right > viewportRect.right + epsilon);
        }

        function schedule() {
            cancelAnimationFrame(raf);
            raf = requestAnimationFrame(update);
        }

        function attachIfVisible() {
            const el = scrollRef.current;
            if (!el) return;
            if (el.offsetParent === null) return;
            if (attached) return;

            attached = true;

            update();
            el.addEventListener('scroll', schedule, { passive: true });

            ro = new ResizeObserver(schedule);
            ro.observe(el);
        }

        function detach() {
            const el = scrollRef.current;
            if (!el) return;
            if (!attached) return;

            attached = false;

            el.removeEventListener('scroll', schedule);
            ro?.disconnect();
            ro = null;
        }

        const visRo = new ResizeObserver(() => {
            const el = scrollRef.current;
            if (!el) return;

            if (el.offsetParent === null) detach();
            else attachIfVisible();
        });

        const el0 = scrollRef.current;
        if (el0) visRo.observe(el0);

        attachIfVisible();

        return () => {
            cancelAnimationFrame(raf);
            detach();
            visRo.disconnect();
        };
    }, [items.length]);

    return (
        <div className={className} data-ui-slider>
            <UiSliderNavigation
                className={navigationClassName}
                canScrollLeft={canScrollLeft}
                canScrollRight={canScrollRight}
                onPrev={scrollPrev}
                onNext={scrollNext}
            />

            <div className="overflow-x-hidden">
                <div
                    ref={scrollRef}
                    tabIndex={0}
                    role="region"
                    aria-roledescription="carousel"
                    aria-label="Slider"
                    onKeyDown={onKeyDown}
                    className="overflow-x-auto snap-x snap-mandatory scrollbar-hidden -mx-2 -lg:mx-3"
                >
                    <ul id={listId} className={cn('flex', listClassName)}>
                        {items.map((child, i) => {
                            const key = isValidElement(child) && child.key != null ? String(child.key) : String(i);

                            return (
                                <li
                                    key={key}
                                    className={cn(
                                        'shrink-0 snap-start px-2 lg:px-3',
                                        'basis-[80%] md:basis-[55%] xl:basis-1/3',
                                        itemClassName,
                                    )}
                                >
                                    {child}
                                </li>
                            );
                        })}
                    </ul>
                </div>
            </div>
        </div>
    );
}
